import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from '@/hooks/use-toast';

export interface JournalEntry {
  id?: string;
  date: string;
  mood: string;
  gratitude: string;
  reflection: string;
  needsFromAllah: string;
  title?: string;
  tags?: string[];
}

export const useJournal = () => {
  const { user, isGuest } = useAuth();
  const { toast } = useToast();
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load entries
  useEffect(() => {
    setEntries([]);
    setIsLoading(true);

    const loadEntries = async () => {
      if (!user && !isGuest) {
        setIsLoading(false);
        return;
      }

      if (isGuest) {
        const saved = localStorage.getItem('ramadanJournalEntries');
        if (saved) {
          try {
            setEntries(JSON.parse(saved));
          } catch {
            setEntries([]);
          }
        }
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('daily_logs')
          .select('*')
          .eq('user_id', user.id)
          .order('date', { ascending: false });

        if (error) throw error;

        const mapped = (data || []).map(row => ({
          id: row.id,
          date: row.date,
          mood: row.mood || '',
          gratitude: row.gratitude || '',
          reflection: row.reflection || '',
          needsFromAllah: row.needs_from_allah || '',
          title: (row as any).title || '',
          tags: (row as any).tags || []
        }));

        setEntries(mapped);
      } catch (error) {
        console.error('Error loading journal:', error);
      }
      setIsLoading(false);
    };

    loadEntries();
  }, [user, isGuest]);

  const saveEntry = useCallback(async (entry: JournalEntry) => {
    if (isGuest || !user) {
      const updated = [...entries.filter(e => e.date !== entry.date), entry];
      setEntries(updated);
      localStorage.setItem('ramadanJournalEntries', JSON.stringify(updated));
      return;
    }

    try {
      const { error } = await supabase
        .from('daily_logs')
        .upsert({
          user_id: user.id,
          date: entry.date,
          mood: entry.mood,
          gratitude: entry.gratitude,
          reflection: entry.reflection,
          needs_from_allah: entry.needsFromAllah,
          title: entry.title || null,
          tags: entry.tags || []
        } as any, { onConflict: 'user_id,date' });

      if (error) throw error;

      // Refresh entries
      const { data } = await supabase
        .from('daily_logs')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: false });

      if (data) {
        const mapped = data.map(row => ({
          id: row.id,
          date: row.date,
          mood: row.mood || '',
          gratitude: row.gratitude || '',
          reflection: row.reflection || '',
          needsFromAllah: row.needs_from_allah || '',
          title: (row as any).title || '',
          tags: (row as any).tags || []
        }));
        setEntries(mapped);
      }

      toast({
        title: 'Entry saved',
        description: 'Your reflection has been saved.'
      });
    } catch (error) {
      console.error('Error saving journal:', error);
      toast({
        title: 'Error saving',
        description: 'Your entry could not be saved. Please try again.',
        variant: 'destructive'
      });
    }
  }, [user, isGuest, entries, toast]);

  const deleteEntry = useCallback(async (date: string) => {
    if (isGuest || !user) {
      const updated = entries.filter(e => e.date !== date);
      setEntries(updated);
      localStorage.setItem('ramadanJournalEntries', JSON.stringify(updated));
      toast({
        title: 'Entry deleted',
        description: 'Your journal entry has been removed.'
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('daily_logs')
        .delete()
        .eq('user_id', user.id)
        .eq('date', date);

      if (error) throw error;

      setEntries(prev => prev.filter(e => e.date !== date));

      toast({
        title: 'Entry deleted',
        description: 'Your journal entry has been removed.'
      });
    } catch (error) {
      console.error('Error deleting journal entry:', error);
      toast({
        title: 'Error deleting',
        description: 'Your entry could not be deleted. Please try again.',
        variant: 'destructive'
      });
    }
  }, [user, isGuest, entries, toast]);

  const getEntryByDate = useCallback((date: string) => {
    return entries.find(e => e.date === date);
  }, [entries]);

  return {
    entries,
    isLoading,
    saveEntry,
    deleteEntry,
    getEntryByDate
  };
};
